import {
  Autocomplete,
  Button,
  Checkbox,
  Grid,
  Image,
  Loader,
  SegmentedControl,
  Select,
  TagsInput,
  TextInput,
} from '@mantine/core'
import { useDebouncedValue } from '@mantine/hooks'
import SaleInput from 'common/inputs/SaleInput'
import NoImage from 'common/NoImage'
import {
  FabricVariantFormInputs,
  PillowVariantFormInputs,
  PremadeVariantFormInputs,
  TableVariantFormInputs,
  TowelVariantFormInputs,
} from 'components/productDetails/VariantForm'
import { useManufacturers, useSearchProducts } from 'hooks/products'
import { FormEvent, useEffect, useState } from 'react'
import { Category, Unit } from 'types/products'
import { useFabricForm, usePillowForm, usePremadeForm, useProductForm, useTableForm, useTowelForm } from './forms'

export default function NewProductForm() {
  const [selectedSymbol, setSelectedSymbol] = useState('')
  const [debouncedSelectedSymbol] = useDebouncedValue(selectedSymbol, 300)
  const [category, setCategory] = useState<Category>(Category.FABRIC)

  const { data: manufacturers } = useManufacturers()

  const products = useSearchProducts({ symbol: debouncedSelectedSymbol })
  const product =
    selectedSymbol && products.data && products.data.data.length !== 0
      ? products.data.data.find((p) => p.symbol === selectedSymbol)
      : undefined

  const { productForm, submitProduct } = useProductForm()
  const { fabricForm, submitFabric } = useFabricForm(product?.id)
  const { premadeForm, submitPremade } = usePremadeForm(product?.id)
  const { pillowForm, submitPillow } = usePillowForm(product?.id)
  const { towelForm, submitTowel } = useTowelForm(product?.id)
  const { tableForm, submitTable } = useTableForm(product?.id)

  const [withoutVariant, setWithoutVariant] = useState(false)

  function getInputProps(key: string, options?: any) {
    if (withoutVariant) return productForm.getInputProps(key, options)
    switch (category) {
      case Category.FABRIC:
        return fabricForm.getInputProps(key, options)
      case Category.PREMADE:
        return premadeForm.getInputProps(key, options)
      case Category.PILLOW:
        return pillowForm.getInputProps(key, options)
      case Category.TOWEL:
        return towelForm.getInputProps(key, options)
      case Category.TABLE:
        return tableForm.getInputProps(key, options)
    }
  }

  function getValue(key: string) {
    switch (category) {
      case Category.FABRIC:
        // @ts-ignore
        return fabricForm.getValues()[key]
      case Category.PREMADE:
        // @ts-ignore
        return premadeForm.getValues()[key]
      case Category.PILLOW:
        // @ts-ignore
        return pillowForm.getValues()[key]
      case Category.TOWEL:
        // @ts-ignore
        return towelForm.getValues()[key]
      case Category.TABLE:
        // @ts-ignore
        return tableForm.getValues()[key]
    }
  }

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    if (withoutVariant) return productForm.onSubmit(submitProduct)(event)
    switch (category) {
      case Category.FABRIC:
        fabricForm.onSubmit(submitFabric)(event)
      case Category.PREMADE:
        premadeForm.onSubmit(submitPremade)(event)
      case Category.PILLOW:
        pillowForm.onSubmit(submitPillow)(event)
      case Category.TOWEL:
        towelForm.onSubmit(submitTowel)(event)
      case Category.TABLE:
        tableForm.onSubmit(submitTable)(event)
    }
  }

  const symbol = getValue('symbol')

  useEffect(() => setSelectedSymbol(symbol), [symbol])

  useEffect(() => {
    if (products.isSuccess) {
      const values = {
        symbol: product?.symbol ?? symbol,
        alternateSymbol: product?.alternateSymbol ?? '',
        image: product?.image ?? '',
        isAtSale: product?.isAtSale ?? false,
        manufacturerId: product?.manufacturer?.id ?? null!,
        availableSizes: product?.availableSizes ?? [],
      }
      setWithoutVariant(false)
      fabricForm.setValues(values)
      premadeForm.setValues(values)
      pillowForm.setValues(values)
      towelForm.setValues(values)
      tableForm.setValues(values)
    }
  }, [product?.symbol])

  useEffect(() => {
    fabricForm.setFieldValue('unit', Unit.METER)
    premadeForm.setFieldValue('unit', Unit.PIECE)
    pillowForm.setFieldValue('unit', Unit.PIECE)
    towelForm.setFieldValue('unit', Unit.PIECE)
    tableForm.setFieldValue('unit', Unit.PIECE)
  }, [category])

  useEffect(() => {
    ;['symbol', 'alternateSymbol', 'image', 'isAtSale', 'manufacturerId', 'availableSizes'].forEach((key) => {
      productForm.setFieldValue(key, getValue(key))
      fabricForm.setFieldValue(key, getValue(key))
      premadeForm.setFieldValue(key, getValue(key))
      pillowForm.setFieldValue(key, getValue(key))
      towelForm.setFieldValue(key, getValue(key))
      tableForm.setFieldValue(key, getValue(key))
    })
  }, [
    getValue('image'),
    getValue('symbol'),
    getValue('alternateSymbol'),
    getValue('isAtSale'),
    getValue('manufacturerId'),
    getValue('availableSizes'),
    withoutVariant,
  ])

  return (
    <form onSubmit={(e) => onSubmit(e)}>
      <Grid my="md">
        <Grid.Col span={4}>
          {product || getValue('image') ? (
            <Image
              src={product?.image ?? getValue('image')}
              fallbackSrc="https://placehold.co/250x250/dee2e6/ffffff?text=Niepoprawny+link&font=roboto"
              alt="Zdjęcie produktu"
              radius="md"
            />
          ) : (
            <NoImage />
          )}
        </Grid.Col>
        <Grid.Col span={8}>
          <Grid>
            <Grid.Col span={6}>
              <Autocomplete
                data={products.data?.data.map(({ symbol }) => symbol) || []}
                rightSection={products.isFetching ? <Loader size={16} /> : null}
                label="Symbol"
                description="Główny symbol produktu, używany przez producenta"
                placeholder="Na przykład 2705"
                withAsterisk
                limit={5}
                filter={({ options }) => options}
                {...getInputProps('symbol')}
              />
            </Grid.Col>
            <Grid.Col span={6}>
              <TextInput
                label="Alternatywny symbol"
                description="Opcjonalny dodatkowy symbol używany wewnętrznie"
                placeholder={product !== undefined ? '' : 'Na przykład Weronika'}
                disabled={product !== undefined}
                {...getInputProps('alternateSymbol')}
              />
            </Grid.Col>
          </Grid>
          <TextInput
            label="Zdjęcie"
            description="Link do zdjęcia produktu, najlepiej zbliżenia wzoru."
            placeholder="https://abcfirany.pl/images/2705.jpg"
            disabled={product !== undefined}
            {...getInputProps('image')}
          />
          <Grid>
            <Grid.Col span={6}>
              <Select
                label="Producent"
                description="Producent produktu"
                placeholder="HAFT"
                data={manufacturers.map((manufacturer) => ({ value: manufacturer.id, label: manufacturer.name }))}
                disabled={product !== undefined}
                withAsterisk
                {...getInputProps('manufacturerId')}
              />
            </Grid.Col>
            <Grid.Col span={6}>
              <TagsInput
                label="Dostępne rozmiary"
                description="Dostępne rozmiary u producenta. Np. 160, 250."
                placeholder={product !== undefined ? '' : 'Wpisz rozmiar i naciśnij Enter'}
                disabled={product !== undefined}
                {...getInputProps('availableSizes')}
              />
            </Grid.Col>
          </Grid>
          <SaleInput mt="md" disabled={product !== undefined} {...getInputProps('isAtSale', { type: 'checkbox' })} />
          <Checkbox
            mt="md"
            label="Dodaj produkt bez wariantu"
            disabled={product !== undefined}
            checked={withoutVariant}
            onChange={(event) => {
              setWithoutVariant(event.currentTarget.checked)
              if (event.currentTarget.checked) {
                ;[fabricForm, premadeForm, pillowForm, towelForm, tableForm].forEach((form) => form.clearErrors())
              }
            }}
          />
          <SegmentedControl
            fullWidth
            mt="sm"
            withItemsBorders={false}
            radius="xl"
            size="md"
            value={category}
            onChange={(value) => setCategory(value as Category)}
            disabled={withoutVariant}
            data={[
              { value: Category.FABRIC, label: 'Metraż' },
              { value: Category.PREMADE, label: 'Gotowy produkt' },
              { value: Category.PILLOW, label: 'Poszewka' },
              { value: Category.TOWEL, label: 'Ręcznik' },
              { value: Category.TABLE, label: 'Nakrycie stołu' },
            ]}
          />
        </Grid.Col>
      </Grid>
      {category === Category.FABRIC ? (
        <FabricVariantFormInputs disabled={withoutVariant} form={fabricForm} />
      ) : category === Category.PREMADE ? (
        <PremadeVariantFormInputs disabled={withoutVariant} form={premadeForm} />
      ) : category === Category.PILLOW ? (
        <PillowVariantFormInputs disabled={withoutVariant} form={pillowForm} />
      ) : category === Category.TOWEL ? (
        <TowelVariantFormInputs disabled={withoutVariant} form={towelForm} />
      ) : category === Category.TABLE ? (
        <TableVariantFormInputs disabled={withoutVariant} form={tableForm} />
      ) : null}
      <Button mt="md" type="submit" variant="filled" fullWidth>
        Dodaj produkt
      </Button>
    </form>
  )
}
